import { createTheme, Theme } from "@mui/material/styles";
import { heatReportColors } from "./colors";

/**
 * Heat Report Module Theme Extension
 *
 * This theme extends the base MUI theme with heat report-specific color palette.
 * Import this theme in heat report module pages to access custom colors via theme.palette.heatReport.*
 */

// Extend the MUI Palette interface
declare module "@mui/material/styles" {
  interface Palette {
    heatReport: {
      assets: {
        eaf: string;
        lmf: string;
        caster: string;
        lab: string;
        default: string;
      };
      status: {
        scheduled: string;
        melting: string;
        tapped: string;
        opened: string;
        closed: string;
        returned: string;
        finished: string;
        unknown: string;
      };
    };
  }

  interface PaletteOptions {
    heatReport?: {
      assets?: {
        eaf?: string;
        lmf?: string;
        caster?: string;
        lab?: string;
        default?: string;
      };
      status?: {
        scheduled?: string;
        melting?: string;
        tapped?: string;
        opened?: string;
        closed?: string;
        returned?: string;
        finished?: string;
        unknown?: string;
      };
    };
  }
}

/**
 * Theme extension with heat report specific colors
 */
export const heatReportThemeExtension = {
  palette: {
    heatReport: heatReportColors,
  },
};

/**
 * Create the heat report theme with custom palette
 */
export const heatReportTheme = createTheme({
  palette: {
    heatReport: heatReportColors,
  },
});

/**
 * Create a complete theme with heat report extensions
 * Use this if you need a standalone theme
 */
export const createHeatReportTheme = (baseTheme?: Theme): Theme => {
  return createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme?.palette,
      ...heatReportThemeExtension.palette,
    },
  });
};

/**
 * Helper function to get heat report colors from any theme
 * Usage: getHeatReportColors(theme).assets.eaf
 */
export const getHeatReportColors = (theme: Theme) => {
  return theme.palette.heatReport;
};

/**
 * Asset ID to color key mapping
 */
const assetIdMap: { [key: number]: keyof typeof heatReportColors.assets } = {
  11: "eaf",
  12: "lmf",
  13: "caster",
  15: "lab",
};

/**
 * Get asset color - falls back to colors constant if theme extension not available
 */
export const getAssetColor = (theme: Theme, assetId: number): string => {
  const key = assetIdMap[assetId];
  if (key) {
    // Try theme first, fall back to colors constant
    return (
      theme.palette.heatReport?.assets?.[key] ?? heatReportColors.assets[key]
    );
  }
  return theme.palette.grey[300];
};

/**
 * Status code to color key mapping
 */
const statusCodeMap: { [key: string]: keyof typeof heatReportColors.status } = {
  S: "scheduled",
  M: "melting",
  T: "tapped",
  O: "opened",
  C: "closed",
  R: "returned",
  F: "finished",
};

/**
 * Get status color - falls back to colors constant if theme extension not available
 */
export const getStatusColor = (theme: Theme, status: string): string => {
  const key = statusCodeMap[status];
  if (key) {
    // Try theme first, fall back to colors constant
    return (
      theme.palette.heatReport?.status?.[key] ?? heatReportColors.status[key]
    );
  }
  return (
    theme.palette.heatReport?.status?.unknown ?? heatReportColors.status.unknown
  );
};
